import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import AxiosUtil from '../../Utils/AxiosUtil';
import type { ColumnsType } from 'antd/es/table';
import {
    Alert as ServaAlert,
    Button,
    Checkbox,
    Col,
    Input,
    Row,
    Skeleton,
    Space,
    Spin,
    Table,
    Text,
    Title,
} from '@servahealth/serva-ui-kit';
import {
    buildLNKErrorMessage,
    filterLinkedAdminRows,
    updateLinkedAdminRow,
} from '../Shared/LNKHelper';
import { invalidateReportMenu } from '../Shared/ReportMenuQuery';
import Administration from '../../Constants/Endpoints/Administration';
import AddDelTenantAdminReportRequest from '../../Interfaces/Requests/Administration/AddDelTenantAdminReportRequest';
import GetTenantAdminReportModelsUsingTenantIDRequest from '../../Interfaces/Requests/Administration/GetTenantAdminReportModelsUsingTenantIDRequest';
import GetTenantAdminReportModelsUsingTenantIDResponse from '../../Interfaces/Responses/Administration/GetTenantAdminReportModelsUsingTenantIDResponse';

type TenantReportProps = {
    tenantId: number;
    tenantName: string;
    close: () => void;
};

const TenantReports: React.FC<TenantReportProps> = ({ ...props }) => {
    const queryClient = useQueryClient();

    const [reports, setReports] = useState<GetTenantAdminReportModelsUsingTenantIDResponse[]>([]);
    const [hideUnlinkedReports, setHideUnlinkedReports] = useState(false);
    const [searchText, setSearchText] = useState('');

    const [currentError, setCurrentError] = useState('');

    const { isLoading: isGetTenantReportsLoading, isError: isGetTenantReportsError, error: getTenantReportsError, refetch: refetchTenantReports } = useQuery(
        ['tenant-admin-reports', props.tenantId],
        async () => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_TENANT_ADMIN_REPORT_MODELS_USING_TENANT_ID}`;
                const requestData: GetTenantAdminReportModelsUsingTenantIDRequest = {
                    gtr_tenantid: props.tenantId,
                };
                return await AxiosUtil.get<GetTenantAdminReportModelsUsingTenantIDResponse[], GetTenantAdminReportModelsUsingTenantIDRequest>(url, requestData);
            } catch (err) {
                setCurrentError('GET_TENANT_ADMIN_REPORT_MODELS_USING_TENANT_ID: [Get Tenant Reports] Internal Error: ' + err);
                console.error('GET_TENANT_ADMIN_REPORT_MODELS_USING_TENANT_ID [Get Tenant Reports] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            enabled: props.tenantId > 0,
            onError: (err: unknown) => {
                setCurrentError('GET_TENANT_ADMIN_REPORT_MODELS_USING_TENANT_ID: [Get Tenant Reports] Internal Error: ' + err);
                console.error('GET_TENANT_ADMIN_REPORT_MODELS_USING_TENANT_ID [Get Tenant Reports] Internal Error: ', err);
            },
            onSuccess: (response: GetTenantAdminReportModelsUsingTenantIDResponse[]) => {
                if (response) {
                    setReports(response);
                }
            },
        }
    );

    const { mutate: addRemoveReport, isLoading: isAddRemoveReportLoading, isError: isAddRemoveReportError, error: getAddRemoveReportError } = useMutation(
        async ({ add, report, }: {
            add: boolean;
            report: GetTenantAdminReportModelsUsingTenantIDResponse;
        }) => {
            let responseData = 0;
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.ADD_DEL_TENANT_ADMIN_REPORTS}`;
                const addDelTenantAdminReports: AddDelTenantAdminReportRequest = {
                    id: report.id,
                    reportName: report.reportName,
                    reportGroupName: report.reportGroupName,
                    tenantId: props.tenantId,
                    tenantName: props.tenantName,
                    isAdd: add,
                    isDel: !add,
                };
                const requestData: AddDelTenantAdminReportRequest[] = [ addDelTenantAdminReports, ];
                responseData = await AxiosUtil.post<number, AddDelTenantAdminReportRequest[]>(url, requestData);
            } catch (err) {
                setCurrentError('ADD_DEL_TENANT_ADMIN_REPORTS: [Add/Remove Tenant Reports] Internal Error: ' + err);
                console.error('ADD_DEL_TENANT_ADMIN_REPORTS [Add/Remove Tenant Reports] Internal Error: ', err);
                return Promise.reject(err);
            }
            return { response: responseData, add, report };
        },
        {
            onError: (err: unknown) => {
                setCurrentError('ADD_DEL_TENANT_ADMIN_REPORTS: [Add/Remove Tenant Reports] Internal Error: ' + err);
                console.error('ADD_DEL_TENANT_ADMIN_REPORTS [Add/Remove Tenant Reports] Internal Error: ', err);
            },
            onSuccess: ({ response, add, report }) => {
                if (response) {
                    setReports(updateLinkedAdminRow(reports, report.id, add));
                    void refetchTenantReports();
                    invalidateReportMenu(queryClient);
                }
            },
        }
    );

    const handleCheckboxChange = (checked: boolean, report: GetTenantAdminReportModelsUsingTenantIDResponse) => {
        const params = { add: checked, report: report, };
        addRemoveReport(params);
    };

    const columns: ColumnsType<GetTenantAdminReportModelsUsingTenantIDResponse> = [
        {
            title: 'Linked',
            key: 'isLinked',
            render: (text: unknown, record: GetTenantAdminReportModelsUsingTenantIDResponse) => (
                <>
                    <Checkbox
                        checked={record.isLinked}
                        onChange={(e) => handleCheckboxChange(e.target.checked, record)}
                    ></Checkbox>
                </>
            ),
        },
        { title: 'Report Group Name', dataIndex: 'reportGroupName', key: 'reportGroupName' },
        { title: 'Report Name', dataIndex: 'reportName', key: 'reportName' },
    ];

    const visibleReports = filterLinkedAdminRows(
        reports,
        hideUnlinkedReports,
        searchText.trim(),
        (report, normalizedSearchText) => normalizedSearchText === ''
            || report.reportGroupName.toLowerCase().includes(normalizedSearchText)
            || report.reportName.toLowerCase().includes(normalizedSearchText)
    );

    const handleShowLinkedReports = () => {
        setHideUnlinkedReports(!hideUnlinkedReports);
    };

    const handleSearchTextChange = (aValue: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(aValue.target.value);
    };

    const doSearch = (sValue: string) => {
        setSearchText(sValue);
    };

    const determineWhichError = () => {
        return buildLNKErrorMessage(
            currentError,
            isGetTenantReportsError,
            getTenantReportsError,
            isAddRemoveReportError,
            getAddRemoveReportError
        );
    };

    return (
        <>
            <Spin spinning={isGetTenantReportsLoading || isAddRemoveReportLoading}>
                <div style={{ display: 'table', margin: 'auto', textAlign: 'center', width: '50%' }}>
                {
                    (
                        isGetTenantReportsError ||
                        isAddRemoveReportError
                    )
                    &&
                    (
                        <ServaAlert
                            className={'mb-2'}
                            message={determineWhichError()}
                            showIcon={true}
                            type='error'
                        ></ServaAlert>
                    )
                }
                </div>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '8px', width: '100%'}}>
                    <Text style={{height: '100%', width: '100%'}}>
                        <Title style={{backgroundColor: 'rgb(00, 55, 90)', borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}} level={4}>Manage Tenant Links To Reports</Title>
                    </Text>
                </Space>
                <div style={{ width: '100%', overflowX: 'auto' }}>
                    <Row gutter={16}>
                        <Col span={4}>
                            <Button
                                onClick={handleShowLinkedReports}
                                size='small'
                                style={{
                                    background: 'none',
                                    backgroundColor: 'lightgray',
                                    color: 'blue',
                                    cursor: 'pointer',
                                }}
                                type='text'
                            >
                                {hideUnlinkedReports ? 'Show All Reports' : 'Hide Unlinked Reports'}
                            </Button>
                        </Col>
                        <Col span={4} />
                        <Col span={8}>
                            <Input
                                allowClear={true}
                                value={searchText}
                                search={true}
                                style={{alignContent: 'center', width: '75%'}}
                                onChange={handleSearchTextChange}
                                onSearch={doSearch}
                            ></Input>
                        </Col>
                        <Col span={4} />
                        <Col span={4}><Text type='secondary' italic ellipsis>{props.tenantName}</Text></Col>
                    </Row>
                    <div style={{ padding: '4px' }} />
                    {isGetTenantReportsLoading && reports.length === 0 ? (
                        <Skeleton />
                    ) : (
                        <Table rowKey='id' columns={columns} dataSource={visibleReports} />
                    )}
                </div>
            </Spin>
        </>
    );
};

export default TenantReports;
